import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useMemo } from 'react';
import { commaSeparatedNumbers } from '../../../../utils/commaSeparatedNumbers';
import classes from './scheduleAttainementByRoom.module.scss';
import { RoomData } from './scheduleAttainmentbyRoom.type';
import { ScheduledRoomData } from '../ScheduledRooms/scheduleRoom.type';

interface ScheduleAttainmentByRoomScreenProps {
  scheduleAttainmentByRoomFirstShift: RoomData[];
  scheduleAttainmentByRoomSecondShift: RoomData[];
  scheduledRooms: ScheduledRoomData[];
  loading: boolean;
}

const ScheduleAttainmentByRoomScreen = ({
  scheduleAttainmentByRoomFirstShift,
  scheduleAttainmentByRoomSecondShift,
  scheduledRooms,
  loading
}: ScheduleAttainmentByRoomScreenProps) => {
  // Create a map for quick lookup of scheduled rooms data with safety check
  const scheduledRoomsMap = useMemo(() => {
    if (!scheduledRooms || !Array.isArray(scheduledRooms)) {
      return new Map();
    }
    return new Map(scheduledRooms.map(room => [room.room_long, room]));
  }, [scheduledRooms]);

  // Calculate totals for first shift - sum all values, treat "-" as 0
  const firstShiftTotals = useMemo(() => {
    return scheduleAttainmentByRoomFirstShift.reduce(
      (acc, room) => {
        // Convert "-" to 0, otherwise convert to number
        const jobsValue = room.jobs_required === '-' ? 0 : Number(room.jobs_required) || 0;
        const bottlesValue = room.bottles_required === '-' ? 0 : Number(room.bottles_required) || 0;
        
        return {
          jobs_required: acc.jobs_required + jobsValue,
          bottles_required: acc.bottles_required + bottlesValue
        };
      },
      { jobs_required: 0, bottles_required: 0 }
    );
  }, [scheduleAttainmentByRoomFirstShift]);

  // Calculate totals for second shift - sum all values, treat "-" as 0
  const secondShiftTotals = useMemo(() => {
    return scheduleAttainmentByRoomSecondShift.reduce(
      (acc, room) => {
        // Convert "-" to 0, otherwise convert to number
        const jobsValue = room.jobs_required === '-' ? 0 : Number(room.jobs_required) || 0;
        const bottlesValue = room.bottles_required === '-' ? 0 : Number(room.bottles_required) || 0;
        
        return {
          jobs_required: acc.jobs_required + jobsValue,
          bottles_required: acc.bottles_required + bottlesValue
        };
      },
      { jobs_required: 0, bottles_required: 0 }
    );
  }, [scheduleAttainmentByRoomSecondShift]);

  // Add total row to first shift data
  const firstShiftDataWithTotal = useMemo(() => {
    return [
      ...scheduleAttainmentByRoomFirstShift,
      {
        room_long: 'Total',
        jobs_required: firstShiftTotals.jobs_required,
        bottles_required: firstShiftTotals.bottles_required,
        isTotal: true
      }
    ];
  }, [scheduleAttainmentByRoomFirstShift, firstShiftTotals]);

  // Add total row to second shift data
  const secondShiftDataWithTotal = useMemo(() => {
    return [
      ...scheduleAttainmentByRoomSecondShift,
      {
        room_long: 'Total',
        jobs_required: secondShiftTotals.jobs_required,
        bottles_required: secondShiftTotals.bottles_required,
        isTotal: true
      }
    ];
  }, [scheduleAttainmentByRoomSecondShift, secondShiftTotals]);

  const jobsRequiredBodyTemplate = (rowData: RoomData & { isTotal?: boolean }) => {
    const displayValue = rowData.jobs_required === '-' ? '-' : commaSeparatedNumbers(rowData.jobs_required);
    return rowData.isTotal ? <span className={classes.totalValue}>{displayValue}</span> : displayValue;
  };

  const bottlesRequiredBodyTemplate = (rowData: RoomData & { isTotal?: boolean }) => {
    const displayValue = rowData.bottles_required === '-' ? '-' : commaSeparatedNumbers(rowData.bottles_required);
    return rowData.isTotal ? <span className={classes.totalValue}>{displayValue}</span> : displayValue;
  };

  const roomTemplate = (rowData: RoomData & { isTotal?: boolean }) => {
    return rowData.isTotal ? (
      <span className={classes.totalValue}>{rowData.room_long}</span>
    ) : (
      rowData.room_long
    );
  };

  const firstShiftIsRoomRunningTemplate = (rowData: RoomData & { isTotal?: boolean }) => {
    if (rowData.isTotal) {
      return <span>-</span>;
    }

    // Look up the scheduled room data using the room name
    const scheduledRoom = scheduledRoomsMap.get(rowData.room_long);
    // Get the First shift status, default to 'N' if not found
    const isRunning = scheduledRoom?.First || 'N';

    return <span>{isRunning}</span>;
  };

  const secondShiftIsRoomRunningTemplate = (rowData: RoomData & { isTotal?: boolean }) => {
    if (rowData.isTotal) {
      return <span>-</span>;
    }

    // Look up the scheduled room data using the room name
    const scheduledRoom = scheduledRoomsMap.get(rowData.room_long);
    // Get the Second shift status, default to 'N' if not found
    const isRunning = scheduledRoom?.Second || 'N';

    return <span>{isRunning}</span>;
  };

  const rowClassName = (rowData: RoomData & { isTotal?: boolean }) => {
    return rowData.isTotal ? classes.totalRow : classes.tableRow;
  };

  if (loading === true) {
    return (
      <div className={classes.loadingContainer}>
        <ProgressSpinner className={classes.spinner} />
        <div>Loading Schedule Attainment Calculator by Room data...</div>
      </div>
    );
  }

  return (
    <div className={classes.scheduleAttainementByRoomCard}>
      <div className={classes.scheduleAttainementByRoomContainer}>
        <div className={classes.shiftSection}>
          <div className={classes.shiftHeader}>First Shift</div>
          <DataTable
            value={firstShiftDataWithTotal}
            loading={loading}
            emptyMessage="No data found"
            className={classes.dataTable}
            rowClassName={rowClassName}
            resizableColumns={false}
            columnResizeMode="expand"
            stripedRows={true}
          >
            <Column
              field="room_long"
              header="Room"
              body={roomTemplate}
              style={{ textAlign: 'left' }}
            />
            <Column
              field="jobs_required"
              header="Jobs Required"
              body={jobsRequiredBodyTemplate}
              style={{ textAlign: 'left' }}
            />
            <Column
              field="bottles_required"
              header="Bottles Required"
              body={bottlesRequiredBodyTemplate}
              style={{ textAlign: 'left' }}
            />
            <Column
              header="Is Room Running"
              body={firstShiftIsRoomRunningTemplate}
              style={{ textAlign: 'center' }}
            />
          </DataTable>
        </div>
        <div className={classes.shiftSection}>
          <div className={classes.shiftHeader}>Second Shift</div>
          <DataTable
            value={secondShiftDataWithTotal}
            loading={loading}
            emptyMessage="No data found"
            className={classes.dataTable}
            rowClassName={rowClassName}
            resizableColumns={false}
            columnResizeMode="expand"
            stripedRows={true}
          >
            <Column
              field="room_long"
              header="Room"
              body={roomTemplate}
              style={{ textAlign: 'left' }}
            />
            <Column
              field="jobs_required"
              header="Jobs Required"
              body={jobsRequiredBodyTemplate}
              style={{ textAlign: 'left' }}
            />
            <Column
              field="bottles_required"
              header="Bottles Required"
              body={bottlesRequiredBodyTemplate}
              style={{ textAlign: 'left' }}
            />
            <Column
              header="Is Room Running"
              body={secondShiftIsRoomRunningTemplate}
              style={{ textAlign: 'center' }}
            />
          </DataTable>
        </div>
      </div>
    </div>
  );
};
export default ScheduleAttainmentByRoomScreen;
